/**
 * Stage two — weighted affinity, scored only on pairs that cleared the gates.
 *
 * Faith and character hold 52 % between them, which keeps the stated purpose —
 * courtship toward marriage — at the centre of the arithmetic.
 *
 * The existing four-axis `valuesScore` (loyalty / honesty / passion /
 * commitment) is not replaced. It feeds this model as part of the character
 * dimension, so every screen currently rendering it keeps working.
 */

import type { ProfileAnswers, YesNo } from "../domain/answers";
import type { MatchScales } from "./types";
import { valuesScore } from "./valuesScore";
import { ANCESTRY_MODE } from "./un8.config";

export type AffinityAxis =
  | "faith"
  | "character"
  | "purpose"
  | "home"
  | "ancestry"
  | "temperament";

/** Must sum to 1. */
export const AFFINITY_WEIGHTS: Record<AffinityAxis, number> = {
  faith: 0.3,
  character: 0.22,
  purpose: 0.18,
  home: 0.15,
  ancestry: 0.1,
  temperament: 0.05,
};

export const AFFINITY_AXES: AffinityAxis[] = [
  "faith",
  "character",
  "purpose",
  "home",
  "ancestry",
  "temperament",
];

export interface AffinityBreakdown {
  axes: Record<AffinityAxis, number>; // each 0–100
  /** Weighted total 0–100. */
  total: number;
}

/* ─────────────── helpers ─────────────── */

/** Agreement on a yes/no pair. Unanswered → neutral rather than penalised. */
function agree(a: YesNo, b: YesNo): number | null {
  if (a == null || b == null) return null;
  return a === b ? 100 : 0;
}

/** Mean of the answered items only; null when nothing was answered. */
function meanAnswered(vals: Array<number | null>): number | null {
  const known = vals.filter((v): v is number => v != null);
  if (!known.length) return null;
  return known.reduce((s, v) => s + v, 0) / known.length;
}

/** Closeness of two 10–100 faith percentages. */
function faithCloseness(a: number | null, b: number | null): number | null {
  if (a == null || b == null) return null;
  return Math.max(0, 100 - Math.abs(a - b));
}

/** Jaccard overlap of two code lists, 0–100. */
function overlap(a: string[], b: string[]): number | null {
  if (!a.length || !b.length) return null;
  const setB = new Set(b);
  const shared = a.filter((x) => setB.has(x)).length;
  const union = new Set([...a, ...b]).size;
  return union === 0 ? null : (shared / union) * 100;
}

/* ─────────────── axes ─────────────── */

function faithAxis(a: ProfileAnswers, b: ProfileAnswers): number | null {
  const af = a.faith;
  const bf = b.faith;
  if (!af || !bf) return null;
  return meanAnswered([
    faithCloseness(af.servingImportance, bf.servingImportance),
    agree(af.keepsCommandments, bf.keepsCommandments),
    agree(af.spreadsGospel, bf.spreadsGospel),
    agree(af.hasIdols, bf.hasIdols),
    agree(af.grewUpInChurch, bf.grewUpInChurch),
  ]);
}

function characterAxis(
  a: ProfileAnswers,
  b: ProfileAnswers,
  scalesA?: MatchScales,
  scalesB?: MatchScales,
): number | null {
  const ac = a.character;
  const bc = b.character;
  const parts: Array<number | null> = [];
  if (ac && bc) {
    parts.push(
      agree(ac.peacemaker, bc.peacemaker),
      agree(ac.caringAffectionate, bc.caringAffectionate),
      agree(ac.goodManners, bc.goodManners),
      agree(ac.wantsPeacefulHome, bc.wantsPeacefulHome),
    );
  }
  // Reuse the existing 1–10 pillar alignment when both sides have it.
  if (scalesA && scalesB) parts.push(valuesScore(scalesA, scalesB));
  return meanAnswered(parts);
}

function purposeAxis(a: ProfileAnswers, b: ProfileAnswers): number | null {
  // Free text is never scored. Honesty self-rating is the one numeric signal.
  const ha = a.character?.honestyRating;
  const hb = b.character?.honestyRating;
  if (ha == null || hb == null) return null;
  return Math.max(0, 100 - (Math.abs(ha - hb) / 9) * 100);
}

function homeAxis(a: ProfileAnswers, b: ProfileAnswers): number | null {
  const ah = a.home;
  const bh = b.home;
  if (!ah || !bh) return null;
  return meanAnswered([
    agree(ah.herbKnowledge, bh.herbKnowledge),
    agree(ah.plantsOwnFruitVeg, bh.plantsOwnFruitVeg),
    agree(ah.interestedInFruitForest, bh.interestedInFruitForest),
    agree(ah.preppingForCalamities, bh.preppingForCalamities),
    agree(ah.communityFarming, bh.communityFarming),
    agree(ah.canningAndPreserving, bh.canningAndPreserving),
    agree(ah.survivalSkills, bh.survivalSkills),
  ]);
}

function ancestryAxis(a: ProfileAnswers, b: ProfileAnswers): number | null {
  if (ANCESTRY_MODE === "gate") return null; // already enforced upstream
  const aa = a.ancestry;
  const ba = b.ancestry;
  if (!aa || !ba) return null;
  const aAccepted = aa.accepted ?? [];
  const bAccepted = ba.accepted ?? [];
  const aOwn = aa.own ?? [];
  const bOwn = ba.own ?? [];
  const aAccepts = aAccepted.includes("any") ? 100 : overlap(aAccepted, bOwn);
  const bAccepts = bAccepted.includes("any") ? 100 : overlap(bAccepted, aOwn);
  return meanAnswered([aAccepts, bAccepts]);
}

function temperamentAxis(a: ProfileAnswers, b: ProfileAnswers): number | null {
  const ac = a.character;
  const bc = b.character;
  return meanAnswered([
    ac && bc ? agree(ac.humorous, bc.humorous) : null,
    ac && bc ? agree(ac.homebuilder, bc.homebuilder) : null,
    overlap(a.interests ?? [], b.interests ?? []),
  ]);
}

/* ─────────────── total ─────────────── */

/**
 * Weighted affinity 0–100.
 *
 * Axes with no answers on either side are dropped and the remaining weights
 * re-normalised, so an incomplete profile scores honestly on what it did
 * answer rather than being punished for silence.
 */
export function affinityScore(
  a: ProfileAnswers,
  b: ProfileAnswers,
  scalesA?: MatchScales,
  scalesB?: MatchScales,
): AffinityBreakdown {
  if (!a || !b) {
    return {
      axes: {
        faith: 0,
        character: 0,
        purpose: 0,
        home: 0,
        ancestry: 0,
        temperament: 0,
      },
      total: 0,
    };
  }
  const raw: Record<AffinityAxis, number | null> = {
    faith: faithAxis(a, b),
    character: characterAxis(a, b, scalesA, scalesB),
    purpose: purposeAxis(a, b),
    home: homeAxis(a, b),
    ancestry: ancestryAxis(a, b),
    temperament: temperamentAxis(a, b),
  };

  let weightUsed = 0;
  let total = 0;
  const axes = {} as Record<AffinityAxis, number>;

  for (const axis of AFFINITY_AXES) {
    const v = raw[axis];
    axes[axis] = v == null ? 0 : Math.round(v);
    if (v == null) continue;
    total += v * AFFINITY_WEIGHTS[axis];
    weightUsed += AFFINITY_WEIGHTS[axis];
  }

  return {
    axes,
    total: weightUsed === 0 ? 0 : Math.round(total / weightUsed),
  };
}

export const AFFINITY_LABELS: Record<AffinityAxis, string> = {
  faith: "Faith intensity",
  character: "Character & conduct",
  purpose: "Life purpose alignment",
  home: "Home & self-sufficiency",
  ancestry: "Cultural & background affinity",
  temperament: "Temperament fit",
};
